import type { PiniaPluginContext } from 'pinia'
import { get, set } from 'lodash-es'
import { ev } from '@/consts'

interface PersistStrategy {
  key?: string
  storage?: Storage
  paths?: string[]
}

declare module 'pinia' {
  interface DefineStoreOptionsBase<S, Store> {
    persist?: boolean | PersistStrategy
  }
}

export const persistedState = ({ store, options }: PiniaPluginContext) => {
  const persist = options.persist
  if (!persist) return

  const strategy = {
    key: store.$id,
    storage: localStorage,
    paths: undefined,
    ...(typeof persist === 'boolean' ? {} : persist)
  }

  const savedState = strategy.storage.getItem(strategy.key)

  if (savedState) {
    try {
      const parsed = JSON.parse(savedState)

      // 如果指定了paths，只恢复指定的状态
      if (strategy.paths) {
        const partialState = {}
        for (const path of strategy.paths) {
          if (get(parsed, path) !== undefined) {
            set(partialState, path, get(parsed, path))
          }
        }
        store.$patch(partialState)
        ev.emit('restore', [partialState, store])
      } else {
        store.$patch(parsed)
        ev.emit('restore', [parsed, store])
      }
    } catch (error) {
      console.error('Failed to restore persisted state:', error)
    }
  }

  // 订阅状态变化
  store.$subscribe(
    (mutation, state) => {
      // console.log('state changed', mutation, state)
      try {
        const stateToPersist = strategy.paths
          ? Object.fromEntries(
              strategy.paths.map((path) => [path, state[path]]),
            )
          : JSON.parse(
              JSON.stringify(state, (key, value) => {
                if (key === 'deps' || key === '_rawValue' || key === '_value') {
                  return undefined
                }
                return value
              }),
            )

        strategy.storage.setItem(
          strategy.key || store.$id,
          JSON.stringify(stateToPersist),
        )
      } catch (error) {
        console.error('Failed to persist state:', error)
      }
    },
    { detached: true }
  )
}
